/**
 * Cadastro de usuários.
 *
 * Restrito a admin e diretoria — é a tela que concede acesso ao sistema.
 *
 * A senha provisória é mostrada UMA vez, no momento do cadastro, com botão de
 * copiar: quem cadastra precisa repassá-la, e ela nunca mais é recuperável
 * (fica só o hash). O usuário é obrigado a trocá-la no primeiro acesso.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus, UserPlus } from 'lucide-react';
import { api } from '../lib/api.ts';
import { formatarCpf, iniciais } from '../lib/format.ts';
import { Aviso, Botao, Campo, Card, Esqueleto, Etiqueta } from '../components/ui/index.tsx';
import Folha from '../components/ui/Folha.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

interface UsuarioLinha {
  id: string;
  nome: string;
  email: string | null;
  cpf: string | null;
  papel: string;
  regionalId: string | null;
  regional: string | null;
  codigoVoluntario: string | null;
  ativo: boolean;
  precisaTrocarSenha: boolean;
  ultimoLogin: string | null;
}

interface Resposta {
  usuarios: UsuarioLinha[];
  papeis: string[];
  regionais: { id: string; nome: string }[];
}

const rotuloPapel = (p: string): string =>
  ({
    admin: 'Administrador',
    diretor: 'Diretoria',
    gerente_regional: 'Gerente regional',
    consultor: 'Consultor',
    pos_vendas: 'Pós-vendas',
    financeiro: 'Financeiro',
    rastreamento: 'Rastreamento',
  })[p] ?? p;

export default function Usuarios() {
  const { usuario: eu } = useAuth();
  const qc = useQueryClient();
  const [aberta, setAberta] = useState<'novo' | 'senha' | null>(null);
  const [alvo, setAlvo] = useState<UsuarioLinha | null>(null);
  const [senhaGerada, setSenhaGerada] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get<Resposta>('/usuarios'),
  });

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['usuarios'] });

  const salvar = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.post('/usuarios', v),
    onSuccess: () => {
      invalidar();
      setAberta(null);
      setErro(null);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível cadastrar.'),
  });

  const alterar = useMutation({
    mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
      api.put(`/usuarios/${id}`, v),
    onSuccess: invalidar,
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível salvar.'),
  });

  const redefinir = useMutation({
    mutationFn: ({ id, senhaNova }: { id: string; senhaNova: string }) =>
      api.post(`/usuarios/${id}/senha`, { senhaNova }),
    onSuccess: () => {
      invalidar();
      setAberta(null);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : 'Não foi possível redefinir.'),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Esqueleto key={i} className="h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[13px] text-[var(--muted)]">Administração</p>
          <h2 className="text-[26px] font-semibold text-[var(--fg)]">Usuários</h2>
        </div>
        <Botao
          variante="primario"
          icone={<Plus size={15} />}
          onClick={() => {
            setSenhaGerada(null);
            setErro(null);
            setAberta('novo');
          }}
        >
          Novo usuário
        </Botao>
      </div>

      {erro && <Aviso tom="alerta">{erro}</Aviso>}

      {senhaGerada && (
        <Card className="p-4">
          <p className="mb-2 text-[13px] text-[var(--muted)]">
            Senha provisória — anote agora, ela não poderá ser vista de novo. O usuário será
            obrigado a trocá-la no primeiro acesso.
          </p>
          <div className="flex items-center gap-2">
            <code className="num min-w-0 flex-1 break-all rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2 text-sm">
              {senhaGerada}
            </code>
            <Botao
              tamanho="sm"
              icone={<Copy size={14} />}
              onClick={() => void navigator.clipboard.writeText(senhaGerada)}
            >
              Copiar
            </Botao>
            <Botao tamanho="sm" variante="fantasma" onClick={() => setSenhaGerada(null)}>
              Ocultar
            </Botao>
          </div>
        </Card>
      )}

      <ul className="flex list-none flex-col gap-3 p-0">
        {(data?.usuarios ?? []).map((u) => (
          <Card as="li" key={u.id} className="flex flex-wrap items-center gap-4 p-4">
            <div className="grid size-9 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[10px] font-medium text-[var(--muted)]">
              {iniciais(u.nome)}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--fg)]">
                {u.nome}
                {u.id === eu?.id && <span className="ml-2 text-xs text-[var(--faint)]">(você)</span>}
              </p>
              <p className="num truncate text-xs text-[var(--faint)]">
                {u.email ?? formatarCpf(u.cpf)}
                {u.regional && ` · ${u.regional}`}
                {u.codigoVoluntario && ` · voluntário ${u.codigoVoluntario}`}
              </p>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {!u.ativo && <Etiqueta tom="perigo">inativo</Etiqueta>}
              {u.precisaTrocarSenha && <Etiqueta tom="alerta">senha provisória</Etiqueta>}
              <Etiqueta tom={u.papel === 'admin' ? 'marca' : 'neutro'}>
                {rotuloPapel(u.papel)}
              </Etiqueta>
            </div>

            <div className="flex w-full shrink-0 gap-2 sm:w-auto">
              <Botao
                tamanho="sm"
                icone={<KeyRound size={13} />}
                onClick={() => {
                  setAlvo(u);
                  setErro(null);
                  setAberta('senha');
                }}
              >
                Senha
              </Botao>
              {/* Desativar a si mesmo tranca o próprio acesso — o servidor
                  recusa, e o botão some para não prometer o que não cumpre. */}
              {u.id !== eu?.id && (
                <Botao
                  tamanho="sm"
                  variante={u.ativo ? 'perigo' : 'secundario'}
                  onClick={() => alterar.mutate({ id: u.id, ativo: !u.ativo })}
                  carregando={alterar.isPending}
                >
                  {u.ativo ? 'Desativar' : 'Reativar'}
                </Botao>
              )}
            </div>
          </Card>
        ))}
      </ul>

      <Folha aberta={aberta === 'novo'} titulo="Novo usuário" onFechar={() => setAberta(null)}>
        <FormularioNovo
          papeis={data?.papeis ?? []}
          regionais={data?.regionais ?? []}
          salvando={salvar.isPending}
          onSalvar={(v) => {
            setSenhaGerada(String(v.senhaInicial));
            salvar.mutate(v);
          }}
        />
      </Folha>

      <Folha
        aberta={aberta === 'senha'}
        titulo={`Redefinir senha — ${alvo?.nome ?? ''}`}
        onFechar={() => setAberta(null)}
      >
        <FormularioSenha
          salvando={redefinir.isPending}
          onSalvar={(senhaNova) => {
            if (!alvo) return;
            setSenhaGerada(senhaNova);
            redefinir.mutate({ id: alvo.id, senhaNova });
          }}
        />
      </Folha>
    </div>
  );
}

// ─────────────────────────── Formulários ───────────────────────────

/** Senha provisória legível: sem caracteres que se confundem ao ditar. */
function sugerirSenha(): string {
  const alfabeto = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => alfabeto[b % alfabeto.length]).join('');
}

function FormularioNovo({
  papeis,
  regionais,
  onSalvar,
  salvando,
}: {
  papeis: string[];
  regionais: { id: string; nome: string }[];
  onSalvar: (v: Record<string, unknown>) => void;
  salvando: boolean;
}) {
  const [f, setF] = useState({
    nome: '',
    email: '',
    cpf: '',
    papel: 'consultor',
    regionalId: '',
    codigoVoluntario: '',
    senhaInicial: sugerirSenha(),
  });
  const campo = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((v) => ({ ...v, [k]: e.target.value }));

  return (
    <div className="flex flex-col gap-4">
      <Campo rotulo="Nome completo" value={f.nome} onChange={campo('nome')} required />

      <div className="flex flex-col gap-4 sm:flex-row">
        <Campo rotulo="E-mail" type="email" value={f.email} onChange={campo('email')} />
        <Campo
          rotulo="CPF"
          inputMode="numeric"
          value={f.cpf}
          onChange={campo('cpf')}
          dica="E-mail ou CPF — ao menos um"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-[var(--fg)]" htmlFor="papel">
          Papel
        </label>
        <select
          id="papel"
          value={f.papel}
          onChange={campo('papel')}
          className="anel-foco h-10 w-full rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-base text-[var(--fg)] sm:text-sm"
        >
          {papeis.map((p) => (
            <option key={p} value={p}>
              {rotuloPapel(p)}
            </option>
          ))}
        </select>
      </div>

      {regionais.length > 0 && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[var(--fg)]" htmlFor="regional">
            Regional
          </label>
          <select
            id="regional"
            value={f.regionalId}
            onChange={campo('regionalId')}
            className="anel-foco h-10 w-full rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-base text-[var(--fg)] sm:text-sm"
          >
            <option value="">Sem regional</option>
            {regionais.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </div>
      )}

      <Campo
        rotulo="Código de voluntário (SGA)"
        value={f.codigoVoluntario}
        onChange={campo('codigoVoluntario')}
        dica="Sem ele, as vendas não são atribuídas a esta pessoa"
      />

      <Campo
        rotulo="Senha provisória"
        value={f.senhaInicial}
        onChange={campo('senhaInicial')}
        dica="Será exigida a troca no primeiro acesso"
      />

      <Botao
        variante="primario"
        larguraTotal
        icone={<UserPlus size={15} />}
        carregando={salvando}
        disabled={f.nome.trim().length < 3 || (!f.email && !f.cpf)}
        onClick={() => onSalvar({ ...f })}
      >
        Cadastrar
      </Botao>
    </div>
  );
}

function FormularioSenha({
  onSalvar,
  salvando,
}: {
  onSalvar: (senha: string) => void;
  salvando: boolean;
}) {
  const [senha, setSenha] = useState(sugerirSenha());
  return (
    <div className="flex flex-col gap-4">
      <Campo
        rotulo="Nova senha provisória"
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
        dica="O usuário será obrigado a trocá-la no primeiro acesso"
      />
      <Botao
        variante="primario"
        larguraTotal
        icone={<Check size={15} />}
        carregando={salvando}
        disabled={senha.length < 6}
        onClick={() => onSalvar(senha)}
      >
        Redefinir
      </Botao>
    </div>
  );
}
